import { CareEnvironment, CarePhase } from '../../../../appModel';
import { appliedHealthCareInflation } from '../../../../appliedHealthCareInflation';
import {
  BuildYearSnapshotInput,
  FinalizeDeathBenefitInput,
  MonthComputeInput,
  PolicyProjectionStrategy,
} from '../strategyTypes';
import { DAYS_IN_MONTH } from '../sharedConstants';
import { computeMonthlyCostMetrics, computeNon2AdlDaysThisRow } from '../sharedEligibility';
import {
  consumeWaitingPeriodDaysForEnvironment,
  compoundFactor,
  getCalendarPolicyYear,
  getCareCostAnchorYear,
  getDailyBenefitAmountForEnvironment,
  getFirstContributingPhaseStartYear,
  getPhaseCareMonthsByPhase,
  getMaxMonthlyBenefitAmount,
  getMonthlyBenefitAmountForEnvironment,
  resolveRowPolicyYear,
} from '../sharedPolicyConfig';
import { calculateClientAge } from '../clientAgeCalculation';
import { deriveStandardMilestones } from '../deriveMilestones';
import {
  deriveIneligibilityReason,
  deriveIneligibleDayBreakdown,
} from '../deriveIneligibilityReason';
import { buildPrePolicyMonth } from '../buildPrePolicyMonth';
import { Claimant, JointWaitingPeriod, seedJointWaitingPeriod } from '../jointClaimant';

// ---------------------------------------------------------------------------
// OneAmerica Asset Care — hybrid whole life with AOB/COB LTC riders.
//
// Two pools (Acceleration of Benefits drawn first, then Continuation of
// Benefits), per-pool compound inflation at policy anniversary, monthly cap
// inflated by the AOB rate, flat Face Amount death benefit (depleted dollar-
// for-dollar by AOB drawdown). No retroactive WP lump; EP days are out of
// pocket.
//
// Reimbursement OR cash indemnity:
// - Reimbursement: pay min(eligibleCost, proRatedCap), drawdown from AOB
//   then COB.
// - Indemnity (AOB-only, per illustration p.3/p.13): pay
//   monthlyCap × indemnityPercent × (eligibleDays/30) as cash regardless of
//   actual cost, drawdown from AOB. Once AOB is exhausted the strategy
//   falls back to reimbursement on COB — illustration text says cash
//   indemnity is "Acceleration of Benefits rider only", which we read as
//   "indemnity is unavailable on COB". The reading is defensible but the
//   PDF never spells out the COB-fallback rule literally; see
//   docs/oa-asset-care/field-mapping.md.
//
// Like Asset Flex, Asset Care needs no per-claim or per-month carry-forward
// beyond the universal pools/waitingPeriod/facts/deltas — both state and
// deltas slots are empty.
// ---------------------------------------------------------------------------

// Joint runs carry per-claimant waiting-period balances here. The universal
// MonthlyState.waitingPeriod is single-insured, so for joint we persist both
// spouses' EP balances in strategyState and surface the active claimant's onto
// each row's waitingPeriod slot (keeping per-row identity checks consistent).
// Undefined on single-life — Asset Care needs no carried state there.
export interface OaAssetCareState {
  jointWaitingPeriod?: JointWaitingPeriod;
}
export type OaAssetCareDeltas = Record<string, never>;

interface InflationConfig {
  baseRate: number;
  eobRate: number;
  capYears: number;
}

// Each pool reads its own inflation rate, falling back to the policy-wide
// inflation rate when the pool-specific knob is missing. The fallback
// matters when a policy carries only a single inflation rate
// (`policyInflationProtection`) and not the per-pool overrides — common
// for older uploads and any product whose illustration lists one rate that
// applies to both pools.
function readInflation(ltcPolicy: PolicyFundingSource): InflationConfig {
  const policyWide = ltcPolicy.policyInflationProtection ?? 0;
  return {
    baseRate: ltcPolicy.policyInflationProtectionOnFaceAmount ?? policyWide,
    eobRate: ltcPolicy.policyInflationProtectionOnCOBAmount ?? policyWide,
    capYears: ltcPolicy.policyInflationProtectionYears ?? 0,
  };
}

function readAtIssuePools(ltcPolicy: PolicyFundingSource): {
  baseAtIssue: number;
  eobAtIssue: number;
} {
  return {
    baseAtIssue: ltcPolicy.policyMaximumBenefitAmount ?? 0,
    eobAtIssue: ltcPolicy.policyContinuationBenefitAmount ?? 0,
  };
}

export const oaAssetCareStrategy: PolicyProjectionStrategy<OaAssetCareState, OaAssetCareDeltas> = {
  initClaimState(client, ltcPolicy, jointAnchorPolicyYear): MonthlyState<OaAssetCareState> {
    const { baseAtIssue, eobAtIssue } = readAtIssuePools(ltcPolicy);
    const faceAmount = ltcPolicy.policyMaximumBenefitAmount ?? 0;
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
    const { baseRate, eobRate, capYears } = readInflation(ltcPolicy);
    // Anniversary inflation compounds every policy year regardless of
    // whether a claim has started; the projection only spans claim months,
    // so anniversaries between policy issue and claim start would otherwise
    // be silently skipped. Pre-inflate at claim start so opening pools
    // match the carrier's actual current pool. Tests where the claim starts
    // at policy year 1 keep both factors at 1.0. Joint pre-inflates to the
    // shared anchor (earliest claim start across both spouses).
    const claimStartPolicyYear =
      jointAnchorPolicyYear ?? getCalendarPolicyYear(client as Client, 0);
    const baseAtClaimStart = baseAtIssue * compoundFactor(baseRate, capYears, claimStartPolicyYear);
    const eobAtClaimStart = eobAtIssue * compoundFactor(eobRate, capYears, claimStartPolicyYear);
    const waitingPeriod: WaitingPeriodState = {
      homeDays:
        ltcPolicy.homeCareWaitingPeriodDaysRemaining ?? ltcPolicy.homeCareWaitingPeriodDays ?? 0,
      facilityDays:
        ltcPolicy.facilityCareWaitingPeriodDaysRemaining ??
        ltcPolicy.facilityCareWaitingPeriodDays ??
        0,
    };
    // Joint: seed both spouses' EP from the (shared, cloned) policy so each
    // serves its own elimination period independently. Distinct copies so they
    // decrement separately.
    const isJoint = jointAnchorPolicyYear != null;
    return {
      pools: {
        baseBenefitPool: Math.max(baseAtClaimStart - utilizedToDate, 0),
        extensionOfBenefitsPool: eobAtClaimStart,
        deathBenefit: Math.max(faceAmount, minGuaranteed),
      },
      waitingPeriod,
      strategyState: isJoint ? { jointWaitingPeriod: seedJointWaitingPeriod(waitingPeriod) } : {},
    };
  },

  buildClaimTerms(client, ltcPolicy): ClaimTerms {
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const dailyBenefit = baseMonthly / DAYS_IN_MONTH;
    const { baseRate, eobRate, capYears } = readInflation(ltcPolicy);
    const clientAsClient = client as Client;
    const projectionAnchorYear = getCareCostAnchorYear(clientAsClient);
    const { baseAtIssue, eobAtIssue } = readAtIssuePools(ltcPolicy);
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    return {
      strategyId: 'oa-asset-care',
      nominalDailyBenefit: dailyBenefit,
      nominalDailyBenefitBase: dailyBenefit,
      // Resolve every environment through the same helper the payout math uses,
      // so the displayed cap matches the math. A raw `?? 0` would collapse a
      // sparse/unpopulated env field to a hard $0 the presentation can't rescue
      // (0 isn't nullish), misrepresenting the cap; the helper honors explicit 0
      // and falls back to the max populated daily for sparse imports.
      nominalDailyBenefitByEnvironment: {
        [CareEnvironment.home]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.home,
        ),
        [CareEnvironment.assistedLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.assistedLiving,
        ),
        [CareEnvironment.fullCareFacility]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.fullCareFacility,
        ),
        [CareEnvironment.independentLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.independentLiving,
        ),
      },
      // Asset Care has no single product-wide ACIO knob; populate
      // inflationRate from the AOB rate for legacy/UI compatibility.
      inflationRate: baseRate,
      inflationCapYears: capYears,
      baseBenefitPoolInflationRate: baseRate,
      baseBenefitPoolInflationCapYears: capYears,
      extensionOfBenefitsPoolInflationRate: eobRate,
      extensionOfBenefitsPoolInflationCapYears: capYears,
      baseBenefitPoolAtIssue: baseAtIssue,
      extensionOfBenefitsPoolAtIssue: eobAtIssue,
      // Asset Care has no duration-derived pool model — the carrier ships
      // dollar pools (Face Amount, Continuation of Benefits). Leave the
      // duration fields unset so the UI doesn't render a misleading "× N
      // months" derivation.
      careCostInflationRate: appliedHealthCareInflation(clientAsClient),
      projectionAnchorYear,
      phaseStartYears: { ...(clientAsClient.clientPhasePredictedStartYears ?? {}) } as Record<
        CarePhase,
        number | null
      >,
      phaseCareMonthsByPhase: getPhaseCareMonthsByPhase(clientAsClient),
      firstPhaseStartYear: getFirstContributingPhaseStartYear(clientAsClient),
      policyPremiumStartYear: ltcPolicy.policyPremiumStartYear ?? projectionAnchorYear,
      minimumGuaranteedDeathBenefit: ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0,
      isReimbursement: !isIndemnity,
      isIndemnity,
    };
  },

  buildYearSnapshot(input: BuildYearSnapshotInput): YearSnapshot {
    const { ltcPolicy, policyYear, calendarYearOffset } = input;
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const { baseRate, eobRate, capYears } = readInflation(ltcPolicy);

    const baseFactor = compoundFactor(baseRate, capYears, policyYear);
    const eobFactor = compoundFactor(eobRate, capYears, policyYear);

    const { baseAtIssue, eobAtIssue } = readAtIssuePools(ltcPolicy);
    const inflatedBaseMonthly = baseMonthly * baseFactor;
    // The COB monthly limit grows on the COB rate, independent of AOB — so a
    // 0% AOB / non-zero COB policy still inflates the cap during the COB phase.
    const inflatedEobMonthly = baseMonthly * eobFactor;
    const inflatedAcceleration = baseAtIssue * baseFactor;
    const inflatedEob = eobAtIssue * eobFactor;
    const faceAmount = ltcPolicy.policyMaximumBenefitAmount ?? 0;

    return {
      policyYear,
      calendarYearOffset,
      effectiveDailyBenefit: inflatedBaseMonthly / DAYS_IN_MONTH,
      effectiveDailyBenefitBase: inflatedBaseMonthly / DAYS_IN_MONTH,
      effectiveDailyBenefitRider: inflatedEobMonthly / DAYS_IN_MONTH,
      effectiveFaceAmount: faceAmount,
      effectiveContinuationBenefit: inflatedEob,
      effectiveTotalLtcBenefit: inflatedAcceleration + inflatedEob,
      monthlyBenefitCap: inflatedBaseMonthly,
      continuationMonthlyBenefitCap: inflatedEobMonthly,
      theoreticalLtcPoolCeiling: inflatedAcceleration + inflatedEob,
    };
  },

  computeMonth(
    input: MonthComputeInput<OaAssetCareState, OaAssetCareDeltas>,
  ): MonthlyEntry<OaAssetCareState, OaAssetCareDeltas> {
    const {
      client,
      ltcPolicy,
      phaseCarePlan,
      monthlyCareInput,
      openState,
      priorMonth,
      priorClaimantMonth,
    } = input;
    const { monthIndex, carePhase, monthsIntoPhase, claimant } = monthlyCareInput;
    const clientAsClient = client as Client;
    // Joint-ness is read from the carried state (per-claimant WP present), not
    // the row tag, so a joint run preserves both spouses' balances regardless.
    const jointWaitingPeriodBefore = openState.strategyState.jointWaitingPeriod;
    const isJoint = jointWaitingPeriodBefore != null;
    const activeClaimant: Claimant = claimant ?? 'client';

    // Cost metrics + structural policy year, resolved up front so the
    // pre-policy gate runs before any WP/ADL classification.
    const costMetrics = computeMonthlyCostMetrics(clientAsClient, phaseCarePlan, monthsIntoPhase);
    const calendarYearOffset = Math.floor(monthIndex / 12);
    const policyYear = resolveRowPolicyYear(
      clientAsClient,
      monthIndex,
      input.jointAnchorPolicyYear,
    );

    // Each spouse serves their own elimination period: joint reads the active
    // claimant's balance from strategyState (the universal waitingPeriod slot is
    // rewritten per row and would otherwise carry the prior row's spouse).
    const activeWaitingPeriodBefore: WaitingPeriodState = jointWaitingPeriodBefore
      ? jointWaitingPeriodBefore[activeClaimant]
      : openState.waitingPeriod;
    // The row's openState surfaces the active claimant's WP-before so identity
    // checks + UI read per-claimant. Shared pools pass through unchanged.
    const rowOpenState: MonthlyState<OaAssetCareState> = isJoint
      ? { ...openState, waitingPeriod: activeWaitingPeriodBefore }
      : openState;

    const context: MonthContext = {
      monthIndex,
      policyYear,
      clientAge: calculateClientAge(clientAsClient.intakeSurvey?.clientAge, policyYear),
      calendarYearOffset,
      carePhase,
      monthsIntoPhase,
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      claimant: monthlyCareInput.claimant,
    };

    // Pre-policy gate: a policy cannot pay for care delivered before its
    // premium-start year (policy year < 1). Book the full cost out-of-pocket
    // and carry every balance forward untouched. Placed before WP/ADL
    // classification and all payout math. (Single-life floors policy year at 1
    // upstream, so only joint partner rows can reach here.)
    if (policyYear < 1) {
      const prePolicyFacts: MonthlyCareFacts = {
        nominalCareCost: costMetrics.nominalCareCost,
        effectiveCareCost: costMetrics.effectiveCareCost,
        dailyCareCost: costMetrics.dailyCareCost,
        careCostInflationYears: costMetrics.careCostInflationYears,
        projectedCareYear: costMetrics.projectedCareYear,
        non2AdlDays: 0,
        coverageEligibleDays: 0,
        waitingPeriodDaysConsumedHome: 0,
        waitingPeriodDaysConsumedFacility: 0,
        appliedMonthlyBenefitCap: 0,
        appliedCapPoolSource: 'base',
      };
      return buildPrePolicyMonth<OaAssetCareState, OaAssetCareDeltas>({
        context,
        facts: prePolicyFacts,
        openState: rowOpenState,
        strategyDeltas: {},
      });
    }

    // 1. Day classification (2-ADL bucket → WP → eligible).
    const non2AdlDays = computeNon2AdlDaysThisRow(clientAsClient, phaseCarePlan, monthsIntoPhase);
    const daysAfter2Adl = DAYS_IN_MONTH - non2AdlDays;

    // Asset Care EP: 0 days for informal/home/adult-day; 90 days for
    // facility (AL, nursing, international). Routed by care environment;
    // the existing two-tier WP fields cover both buckets.
    const wpConsumption = consumeWaitingPeriodDaysForEnvironment(
      activeWaitingPeriodBefore,
      phaseCarePlan.appliedCareEnvironment,
      daysAfter2Adl,
    );
    const {
      wpDaysThisRow,
      closeHomeWp,
      closeFacilityWp,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      closeRelevantWp,
    } = wpConsumption;
    const eligibleDays = daysAfter2Adl - wpDaysThisRow;

    // 2. Per-pool monthly dollar caps for this policy year. The AOB cap grows
    //    on the AOB rate, the COB cap on the COB rate — so once AOB is
    //    exhausted and the draw moves to COB, the monthly limit reflects COB
    //    inflation (mirroring the carrier's separate AOB/COB Monthly Benefit
    //    Limit columns) instead of staying pinned to the AOB rate.
    //    `costMetrics` and `policyYear` were resolved up front for the
    //    pre-policy gate.
    const { baseRate, eobRate, capYears } = readInflation(ltcPolicy);

    const baseMonthly = getMonthlyBenefitAmountForEnvironment(
      ltcPolicy,
      phaseCarePlan.appliedCareEnvironment,
    );
    const baseMonthlyCap = baseMonthly * compoundFactor(baseRate, capYears, policyYear);
    const eobMonthlyCap = baseMonthly * compoundFactor(eobRate, capYears, policyYear);

    // 3. Annual anniversary accrual against the AT-ISSUE amount, gated on
    //    "pool still > 0" so an exhausted pool stays exhausted (the carrier
    //    caps lifetime LTC at what was drawn before exhaustion; inflated
    //    ceilings don't reopen a closed pool).
    let baseInflationAccrual = 0;
    let eobInflationAccrual = 0;
    if (priorMonth && policyYear !== priorMonth.context.policyYear) {
      const priorYear = priorMonth.context.policyYear;
      const { baseAtIssue, eobAtIssue } = readAtIssuePools(ltcPolicy);
      const baseFactorDelta =
        compoundFactor(baseRate, capYears, policyYear) -
        compoundFactor(baseRate, capYears, priorYear);
      const eobFactorDelta =
        compoundFactor(eobRate, capYears, policyYear) -
        compoundFactor(eobRate, capYears, priorYear);
      if (openState.pools.baseBenefitPool > 0) {
        baseInflationAccrual = baseAtIssue * baseFactorDelta;
      }
      if (openState.pools.extensionOfBenefitsPool > 0) {
        eobInflationAccrual = eobAtIssue * eobFactorDelta;
      }
    }
    const baseAfterAccrual = openState.pools.baseBenefitPool + baseInflationAccrual;
    const eobAfterAccrual = openState.pools.extensionOfBenefitsPool + eobInflationAccrual;

    // 4. Payout: branches on indemnity vs. reimbursement. Indemnity is
    //    AOB-only — once AOB is exhausted we revert to reimbursement on
    //    COB.
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    const indemnityPercent = ltcPolicy.policyIndemnityBenefitPercent ?? 1;
    const baseProRatedCap = baseMonthlyCap * (eligibleDays / DAYS_IN_MONTH);

    let drawFromBase = 0;
    let drawFromEob = 0;
    let indemnityPaid = 0;
    if (isIndemnity && baseAfterAccrual > 0) {
      // Pay capped indemnity from AOB regardless of actual cost. Drawdown
      // cannot exceed the pool itself (last AOB month may pay less than
      // the capped amount). Indemnity is AOB-only at the AOB cap.
      const indemnityAmount = Math.min(baseProRatedCap * indemnityPercent, baseAfterAccrual);
      drawFromBase = indemnityAmount;
      indemnityPaid = indemnityAmount;
    } else {
      // Reimbursement, or indemnity-with-AOB-exhausted. Reimburse each
      // eligible day up to that pool's daily cap, AOB first then COB. AOB
      // covers as many eligible days as its pool allows (at the AOB cap); the
      // remaining eligible days fall to COB (at the COB cap). When the two
      // rates are equal this reduces exactly to the prior single-cap math.
      const dailyCost = costMetrics.dailyCareCost;
      const baseDailyCap = baseMonthlyCap / DAYS_IN_MONTH;
      const eobDailyCap = eobMonthlyCap / DAYS_IN_MONTH;

      const basePerDay = Math.min(dailyCost, baseDailyCap);
      const baseDaysFunded =
        basePerDay > 0 ? Math.min(eligibleDays, baseAfterAccrual / basePerDay) : 0;
      drawFromBase = basePerDay * baseDaysFunded;

      const eobPerDay = Math.min(dailyCost, eobDailyCap);
      const eobDaysFunded =
        eobPerDay > 0 ? Math.min(eligibleDays - baseDaysFunded, eobAfterAccrual / eobPerDay) : 0;
      drawFromEob = eobPerDay * eobDaysFunded;
    }
    const ltcCoveragePaid = drawFromBase + drawFromEob;

    // 5. Closing balances. Only AOB drawdown depletes the death benefit
    //    (COB is a separate pool and doesn't accelerate the face).
    const closeBase = baseAfterAccrual - drawFromBase;
    const closeEob = eobAfterAccrual - drawFromEob;
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
    const closeDeathBenefit = Math.max(openState.pools.deathBenefit - drawFromBase, minGuaranteed);
    const deathBenefitReduction = openState.pools.deathBenefit - closeDeathBenefit;
    const deathBenefitFloored =
      closeDeathBenefit === minGuaranteed && drawFromBase > deathBenefitReduction;

    // The row's applied cap follows the pool funding it: once AOB is exhausted
    // at close (and COB is engaged), the COB-rate cap governs.
    const inCobPhase = closeBase <= 0 && (closeEob > 0 || drawFromEob > 0);

    const facts: MonthlyCareFacts = {
      nominalCareCost: costMetrics.nominalCareCost,
      effectiveCareCost: costMetrics.effectiveCareCost,
      dailyCareCost: costMetrics.dailyCareCost,
      careCostInflationYears: costMetrics.careCostInflationYears,
      projectedCareYear: costMetrics.projectedCareYear,
      non2AdlDays,
      coverageEligibleDays: eligibleDays,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      appliedMonthlyBenefitCap: inCobPhase ? eobMonthlyCap : baseMonthlyCap,
      appliedCapPoolSource: inCobPhase ? 'eob' : 'base',
    };

    const payout: PayoutSummary = {
      ltcCoveragePaid,
      ltcPayoutToCost: ltcCoveragePaid,
      ineligibleCost: costMetrics.effectiveCareCost - ltcCoveragePaid,
      indemnityPayout: indemnityPaid,
    };

    const deltas: MonthlyDeltas<OaAssetCareDeltas> = {
      payout,
      base: { depletion: drawFromBase, inflationAccrual: baseInflationAccrual },
      eob: { depletion: drawFromEob, inflationAccrual: eobInflationAccrual },
      deathBenefit: {
        reduction: deathBenefitReduction,
        flooredAtMinimum: deathBenefitFloored,
      },
      strategyDeltas: {},
    };

    const activeWaitingPeriodAfter: WaitingPeriodState = {
      homeDays: closeHomeWp,
      facilityDays: closeFacilityWp,
    };
    const closeState: MonthlyState<OaAssetCareState> = {
      pools: {
        baseBenefitPool: closeBase,
        extensionOfBenefitsPool: closeEob,
        deathBenefit: closeDeathBenefit,
      },
      waitingPeriod: activeWaitingPeriodAfter,
      // Carry the other spouse's EP balance forward unchanged; update only the
      // active claimant's.
      strategyState: jointWaitingPeriodBefore
        ? {
            jointWaitingPeriod: {
              ...jointWaitingPeriodBefore,
              [activeClaimant]: activeWaitingPeriodAfter,
            },
          }
        : {},
    };

    const status: MonthlyStatus = {
      ltcPoolExhausted: closeBase + closeEob <= 0,
      clientPoolExhausted: closeBase + closeEob <= 0,
      epSatisfied: closeRelevantWp === 0,
    };

    // Transition milestones (phase/env change, anniversary) compare against
    // `priorClaimantMonth` — the prior row of THIS spouse — not the global
    // previous row, which on a joint policy is often the other spouse (the
    // merged stream interleaves them when their care overlaps) and would fire a
    // spurious transition on every interleaved row. The pool inflation accrual
    // above keeps using the global `priorMonth` so it fires once per shared
    // policy-year boundary, not once per spouse.
    const milestones = deriveStandardMilestones<OaAssetCareState, OaAssetCareDeltas>({
      context,
      facts,
      openState: rowOpenState,
      closeState,
      deltas,
      priorMonth: priorClaimantMonth,
      retroactiveLumpPaid: 0,
      minimumGuaranteedDeathBenefit: minGuaranteed,
      clientHasStartedLtc: clientAsClient.intakeSurvey?.clientHasStartedLtc ?? false,
      hasInflationProtection: baseRate > 0 || eobRate > 0,
    });

    const ineligibilityInput = {
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      facts,
      openState: rowOpenState,
      closeState,
      payout,
    };
    const ineligibilityReason = deriveIneligibilityReason<OaAssetCareState>(ineligibilityInput);
    const ineligibleDayBreakdown =
      deriveIneligibleDayBreakdown<OaAssetCareState>(ineligibilityInput);

    return {
      context,
      facts,
      openState: rowOpenState,
      state: closeState,
      deltas,
      events: { status, milestones, ineligibilityReason, ineligibleDayBreakdown },
    };
  },

  finalizeDeathBenefit({ closingState }: FinalizeDeathBenefitInput<OaAssetCareState>): number {
    return closingState.pools.deathBenefit;
  },
};
